import React, { useState, useEffect, useMemo, createContext, useContext, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Main, Expertise, Project, Contact, Navigation, Footer, NotFound } from './components';
import ScrollToTop from './components/ScrollToTop';
import { ClickFeedback, PageLoading, ReadingProgress, RouteBoundary, RouteMotion, useScrollReveal } from './components/Motion';
import './index.scss';

const BlogDetail = lazy(() => import('./components/BlogDetail'));

export const ThemeContext = createContext<{ mode: string; toggleMode: () => void }>({ mode: 'dark', toggleMode: () => {} });
export const useThemeMode = () => useContext(ThemeContext);

function RoutePosition() {
  const location = useLocation();
  useEffect(() => {
    const legacy = (location.state as { targetId?: string } | null)?.targetId;
    const id = location.hash.slice(1) || legacy;
    let observer: MutationObserver | undefined;
    const frame = requestAnimationFrame(() => {
      if (!id) { window.scrollTo({ top: 0, behavior: 'auto' }); return; }
      const scrollToTarget = () => {
        const target = document.getElementById(id === 'history' ? 'about' : id);
        target?.scrollIntoView({ behavior: 'auto' });
        return Boolean(target);
      };
      if (!scrollToTarget()) {
        // An article heading may arrive after its route chunk finishes loading.
        observer = new MutationObserver(() => { if (scrollToTarget()) observer?.disconnect(); });
        observer.observe(document.getElementById('main-content') || document.body, { childList: true, subtree: true });
      }
    });
    return () => { cancelAnimationFrame(frame); observer?.disconnect(); };
  }, [location.key, location.pathname, location.hash, location.state]);
  return null;
}

function Home() {
  const reveal = useScrollReveal<HTMLElement>('', '[data-reveal]:not([data-reveal="row"])');
  return <main id="main-content" ref={reveal}><div className="home-layout page-width"><Main /><Project /></div><Expertise /><Contact /></main>;
}

export default function App() {
  const [mode, setMode] = useState<'dark' | 'light'>(() => {
    try { return localStorage.getItem('theme') === 'light' ? 'light' : 'dark'; } catch { return 'dark'; }
  });
  const toggleMode = () => setMode(previous => previous === 'dark' ? 'light' : 'dark');
  useEffect(() => {
    document.documentElement.dataset.theme = mode;
    try { localStorage.setItem('theme', mode); } catch { /* The theme also works without storage. */ }
  }, [mode]);
  const theme = useMemo(() => createTheme({
    palette: { mode, primary: { main: mode === 'dark' ? '#8ac9c2' : '#236d66' }, background: { default: mode === 'dark' ? '#141617' : '#f8f8f4', paper: mode === 'dark' ? '#1b1e1f' : '#ffffff' }, text: { primary: mode === 'dark' ? '#f0f0e9' : '#222728', secondary: mode === 'dark' ? '#a3aaa8' : '#5b6662' } },
    typography: { fontFamily: 'Manrope, sans-serif', button: { textTransform: 'none' } },
  }), [mode]);
  return <ThemeContext.Provider value={{ mode, toggleMode }}><ThemeProvider theme={theme}><div className={`main-container ${mode}-mode`}><Router><a className="skip-link" href="#main-content">Skip to content</a><ReadingProgress /><Navigation /><RoutePosition /><RouteMotion><RouteBoundary><Routes><Route path="/" element={<Home />} /><Route path="/blog/:slug" element={<main id="main-content"><Suspense fallback={<PageLoading />}><BlogDetail /></Suspense></main>} /><Route path="*" element={<main id="main-content"><NotFound /></main>} /></Routes></RouteBoundary></RouteMotion><Footer /><ScrollToTop /><ClickFeedback /></Router></div></ThemeProvider></ThemeContext.Provider>;
}
